import { Globe, Check, X, ChevronDown, Search } from 'lucide-react';
import React, { useCallback, useState, useMemo, useEffect } from 'react';

import { Input } from '@/components/design-system';
import {
  COLOR_SCALE,
  FLEX_ALIGN as FLEX_ALIGN,
  FLEX_JUSTIFY as FLEX_JUSTIFY,
  FLEX_SHRINK,
  FLEX,
  _INLINE_FLEX,
  FONT_WEIGHTS,
  GAP_SCALE,
  INTERACTIVE_STATES,
  MARGIN_BOTTOM,
  MARGIN_LEFT,
  MARGIN_TOP,
  MARGIN_X,
  PADDING_BOTTOM,
  PADDING_LEFT,
  PADDING_RIGHT,
  PADDING_X,
  PADDING_Y,
  POSITION_SCALE,
  RADIUS_SCALE,
  SHADOW_SCALE,
  SIZE_SCALE,
  SPACE_X_SCALE,
  SPACE_Y_SCALE,
  SURFACE_PADDING,
  TEXT_ALIGNMENT,
  TEXT_TRANSFORM,
  TYPE_SCALE,
} from '@/components/design-system/tokens';
import { languages } from '@/components/layout/constants';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useCurrency } from '@/hooks/useCurrency';
import { useTranslation } from '@/hooks/useTranslation';
import { ensureLocale } from '@/utils';

import { useEscapeKey, useArrowNavigation } from '../hooks/useKeyboardNav';
import { useLanguageNotification } from '../hooks/useLanguageNotification';

// Helper function to get currency symbol
const getCurrencySymbol = (currencyCode: string): string => {
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find(part => part.type === 'currency');
    return currencyPart?.value ?? '';
  } catch {
    return '';
  }
};

// Helper function to get currency display names
const getDisplayNames = (locale: string): Intl.DisplayNames | null => {
  if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
    try {
      return new Intl.DisplayNames([locale], { type: 'currency' });
    } catch {
      // Intl.DisplayNames not available for locale
    }
  }
  return null;
};

interface PreferencesSelectorProps {
  languageDropdownOpen: boolean;
  setLanguageDropdownOpen: (open: boolean) => void;
  languageDropdownRef: React.RefObject<HTMLDivElement>;
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  headerOnLight: boolean;
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
}

// Helper component for language notification
const LanguageNotification = React.memo(
  ({
    showNotification,
    dismissNotification,
    handleKeepLanguage,
    handleLanguageChangeFromNotification,
    currentLanguage,
    languages,
    t,
  }: {
    showNotification: boolean;
    dismissNotification: () => void;
    handleKeepLanguage: () => void;
    handleLanguageChangeFromNotification: () => void;
    currentLanguage: string;
    languages: typeof import('@/components/layout/constants').languages;
    t: (
      page: string,
      context: string,
      fallback?: string | null,
      variables?: Record<string, unknown>
    ) => string;
  }) => {
    if (!showNotification) return null;

    return (
      <div
        className={`absolute top-full right-0 ${MARGIN_TOP['2']} ${SIZE_SCALE.w80} ${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} ${SHADOW_SCALE['2xl']} border ${COLOR_SCALE['admin-border']} ${SURFACE_PADDING['4']} z-[1000]`}
      >
        <div
          className={`${FLEX.flex} ${FLEX_ALIGN.start} ${FLEX_JUSTIFY.between} ${MARGIN_BOTTOM['3']}`}
        >
          <div className={`${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['2']}`}>
            <div
              className={`${SIZE_SCALE.w8} ${SIZE_SCALE.h8} ${RADIUS_SCALE.full} ${COLOR_SCALE['gray-900']} ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center}`}
            >
              <Globe className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4} text-white`} />
            </div>
            <div>
              <h4 className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
                {t('notification', 'language_available', 'Language Options')}
              </h4>
            </div>
          </div>
          <button
            onClick={dismissNotification}
            className={`${COLOR_SCALE['icon-muted']} hover:${COLOR_SCALE['foreground-muted']} transition-colors ${SURFACE_PADDING['1']} hover:${COLOR_SCALE['surface-subtle']} ${RADIUS_SCALE.lg}`}
            aria-label={t('aria', 'close', 'Close')}
          >
            <X className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4}`} />
          </button>
        </div>

        <p className={`${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']} ${MARGIN_BOTTOM['4']}`}>
          {t('notification', 'language_message', "You're viewing this site in")}{' '}
          <span className={`${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
            {languages.find(l => l.code === currentLanguage)?.name ?? currentLanguage.toUpperCase()}
          </span>
          . {t('notification', 'language_question', 'Would you like to continue in this language?')}
        </p>

        <div className={`${FLEX.flex} ${FLEX.flexCol} ${SPACE_Y_SCALE['2']}`}>
          <button
            onClick={handleKeepLanguage}
            className={`${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center} ${SPACE_X_SCALE['2']} ${PADDING_X['4']} ${PADDING_Y['2.5']} ${COLOR_SCALE['shortcuts-bg']} hover:${COLOR_SCALE['shortcuts-hover']} ${COLOR_SCALE['shortcuts-text']} ${RADIUS_SCALE.lg} ${INTERACTIVE_STATES.transitions.all} ${TYPE_SCALE.sm} ${FONT_WEIGHTS.medium}`}
          >
            <Check className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4}`} />
            <span>{t('notification', 'keep_language', 'Yes, continue')}</span>
          </button>
          <button
            onClick={handleLanguageChangeFromNotification}
            className={`${PADDING_X['4']} ${PADDING_Y['2.5']} ${COLOR_SCALE.surface} hover:${COLOR_SCALE['surface-muted']} ${COLOR_SCALE.foreground} border ${COLOR_SCALE['admin-border']} hover:${COLOR_SCALE['gray-400']} ${RADIUS_SCALE.lg} transition-colors text-sm font-medium`}
          >
            {t('notification', 'change_language', 'Change language')}
          </button>
        </div>
      </div>
    );
  }
);

LanguageNotification.displayName = 'LanguageNotification';

// Custom hook for currency-related logic
const useCurrencyLogic = (languageDropdownOpen: boolean, activeTab: string) => {
  const { selectedCurrency, availableCurrencies, setSelectedCurrency } = useCurrency();
  const { currentLanguage: translationLanguage } = useTranslation();
  const [currencySearch, setCurrencySearch] = useState('');

  // Get currency symbol for selected currency
  const selectedCurrencySymbol = useMemo(
    () => getCurrencySymbol(selectedCurrency),
    [selectedCurrency]
  );

  // Get resolved locale for currency display names
  const resolvedLocale = useMemo(
    () => ensureLocale(translationLanguage, 'en') ?? 'en',
    [translationLanguage]
  );

  // Get currency display names
  const displayNames = useMemo(() => getDisplayNames(resolvedLocale), [resolvedLocale]);

  // Filter currencies based on search
  const filteredCurrencies = useMemo(() => {
    const query = currencySearch.trim().toLowerCase();
    if (!query) {
      return availableCurrencies;
    }

    return availableCurrencies.filter(code => {
      if (!code) return false;
      // Search by currency code
      if (code.toLowerCase().includes(query)) return true;

      // Search by currency display name
      const displayName = displayNames?.of(code);
      if (displayName?.toLowerCase().includes(query)) {
        return true;
      }

      // Search by currency symbol
      const symbol = getCurrencySymbol(code);
      if (symbol.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  }, [availableCurrencies, displayNames, currencySearch]);

  // Reset search when switching tabs or closing dropdown
  useEffect(() => {
    if (!languageDropdownOpen) {
      setCurrencySearch('');
    }
  }, [languageDropdownOpen]);

  useEffect(() => {
    if (activeTab !== 'currency') {
      setCurrencySearch('');
    }
  }, [activeTab]);

  return {
    selectedCurrency,
    setSelectedCurrency,
    currencySearch,
    setCurrencySearch,
    selectedCurrencySymbol,
    filteredCurrencies,
    displayNames,
  };
};

const PreferencesSelector = React.memo(
  ({
    languageDropdownOpen,
    setLanguageDropdownOpen,
    languageDropdownRef,
    currentLanguage,
    changeLanguage,
    headerOnLight,
    t,
  }: PreferencesSelectorProps) => {
    const [activeTab, setActiveTab] = useState('language'); // 'language' or 'currency'

    const {
      selectedCurrency,
      setSelectedCurrency,
      currencySearch,
      setCurrencySearch,
      selectedCurrencySymbol,
      filteredCurrencies,
      displayNames,
    } = useCurrencyLogic(languageDropdownOpen, activeTab);

    // Language notification on first visit
    const { showNotification, dismissNotification } = useLanguageNotification();

    // Close handler
    const closeDropdown = useCallback(() => {
      setLanguageDropdownOpen(false);
      setActiveTab('language');
      setCurrencySearch('');
    }, [setLanguageDropdownOpen, setCurrencySearch]);

    // Handle language change from notification
    const handleLanguageChangeFromNotification = useCallback(() => {
      dismissNotification();
      setLanguageDropdownOpen(true);
    }, [dismissNotification, setLanguageDropdownOpen]);

    // Handle "keep current language" action
    const handleKeepLanguage = useCallback(() => {
      dismissNotification();
    }, [dismissNotification]);

    // Handle currency selection
    const handleCurrencySelect = useCallback(
      (currency: string) => {
        setSelectedCurrency(currency);
        closeDropdown();
      },
      [setSelectedCurrency, closeDropdown]
    );

    // Keyboard navigation
    useEscapeKey(languageDropdownOpen, closeDropdown);
    const itemCount = activeTab === 'language' ? languages.length : filteredCurrencies.length;
    useArrowNavigation(languageDropdownOpen, itemCount, languageDropdownRef);

    const _themeClasses = headerOnLight
      ? `${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-subtle']}/70`
      : `text-white/80 hover:text-white hover:${COLOR_SCALE.surface}/20`;

    return (
      <div className={POSITION_SCALE.relative} ref={languageDropdownRef}>
        <div className={POSITION_SCALE.relative}>
          <button
            onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
            className={`text-xs ${FONT_WEIGHTS.medium} transition-all duration-300 ${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['2']} whitespace-nowrap ${PADDING_X['4']} ${PADDING_Y['2']} rounded-none !border-none outline-none ring-0 focus:outline-none focus:ring-0 focus:!border-none focus-visible:!border-none shadow-none focus:shadow-none focus-visible:shadow-none active:shadow-none bg-transparent hover:bg-transparent ${headerOnLight ? COLOR_SCALE['foreground-muted'] : 'text-white/80'}`}
            style={{
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
            }}
            aria-label={t('aria', 'preferences', 'Language and Currency preferences')}
          >
            <Globe className={`${SIZE_SCALE.w3} ${SIZE_SCALE['3']} flex-shrink-0`} />
            <span className={`uppercase ${FONT_WEIGHTS.semibold}`}>{currentLanguage}</span>
            <span className={`${TYPE_SCALE['3xs']} opacity-60`}>|</span>
            <span
              className={`uppercase ${FONT_WEIGHTS.semibold} flex items-center ${GAP_SCALE['1']}`}
            >
              {selectedCurrencySymbol && (
                <span className={`${TEXT_TRANSFORM.none}`}>{selectedCurrencySymbol}</span>
              )}
              {selectedCurrency}
            </span>
            <ChevronDown
              className={`${SIZE_SCALE.w3} ${SIZE_SCALE['3']} transition-transform duration-200 ${languageDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Pulse indicator for notification */}
          {showNotification && (
            <span className={`absolute -top-1 -right-1 ${FLEX.flex} ${SIZE_SCALE.iconXs}`}>
              <span
                className={`animate-ping absolute inline-flex h-full w-full ${RADIUS_SCALE.full} ${COLOR_SCALE['gray-900']} opacity-75`}
              ></span>
              <span
                className={`relative ${_INLINE_FLEX.inlineFlex} ${RADIUS_SCALE.full} ${SIZE_SCALE.iconXs} ${COLOR_SCALE['gray-900']}`}
              ></span>
            </span>
          )}
        </div>

        {/* Language notification popover */}
        <LanguageNotification
          showNotification={showNotification && !languageDropdownOpen}
          dismissNotification={dismissNotification}
          handleKeepLanguage={handleKeepLanguage}
          handleLanguageChangeFromNotification={handleLanguageChangeFromNotification}
          currentLanguage={currentLanguage}
          languages={languages}
          t={t}
        />

        {languageDropdownOpen && (
          <div
            className={`absolute top-full right-0 ${MARGIN_TOP['2']} ${SIZE_SCALE.w80} max-w-[calc(100vw-2rem)] ${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} shadow-2xl border ${COLOR_SCALE['admin-border']} z-[1000]`}
            role="menu"
            aria-label={t('aria', 'preferences_menu', 'Preferences menu')}
          >
            {/* Tab Headers */}
            <div
              className={`${FLEX.flex} ${GAP_SCALE['1']} ${SURFACE_PADDING['1.5']} ${COLOR_SCALE['surface-muted']} rounded-t-xl`}
            >
              <button
                onClick={() => setActiveTab('language')}
                className={`flex-1 ${PADDING_X['4']} ${PADDING_Y['2']} text-xs font-semibold transition-all ${RADIUS_SCALE.lg} ${
                  activeTab === 'language'
                    ? `${COLOR_SCALE.surface} ${COLOR_SCALE.foreground} shadow-sm`
                    : `${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE.foreground} hover:${COLOR_SCALE.surface}/50`
                }`}
              >
                {t('preferences', 'language', 'Language')}
              </button>
              <button
                onClick={() => setActiveTab('currency')}
                className={`flex-1 ${PADDING_X['4']} ${PADDING_Y['2']} text-xs font-semibold transition-all ${RADIUS_SCALE.lg} ${
                  activeTab === 'currency'
                    ? `${COLOR_SCALE.surface} ${COLOR_SCALE.foreground} shadow-sm`
                    : `${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE.foreground} hover:${COLOR_SCALE.surface}/50`
                }`}
              >
                {t('preferences', 'currency', 'Currency')}
              </button>
            </div>

            {/* Tab Content */}
            <div
              className={`max-h-80 overflow-y-auto overflow-x-hidden ${PADDING_Y['2']} scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-gray-500`}
            >
              {activeTab === 'language' ? (
                // Language options
                <>
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        closeDropdown();
                      }}
                      className={`${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${PADDING_X['4']} ${PADDING_Y['3']} hover:${COLOR_SCALE['surface-subtle']} text-sm ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.foreground} transition-colors w-full text-left ${RADIUS_SCALE.md} ${MARGIN_X['1']} focus:outline-none ${INTERACTIVE_STATES.focus.ring} focus-visible:ring-inset focus-visible:ring-gray-400 focus-visible:ring-offset-0`}
                      role="menuitem"
                      aria-current={currentLanguage === lang.code ? 'true' : 'false'}
                    >
                      <div
                        className={`${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['3']} ${SIZE_SCALE.minW0} ${FLEX.flex1}`}
                      >
                        <span className={`${TYPE_SCALE.lg} flex-shrink-0`}>{lang.flag}</span>
                        <div className={`${SIZE_SCALE.minW0} ${FLEX.flex1}`}>
                          <div className={`${FONT_WEIGHTS.medium} break-words leading-snug`}>
                            {lang.name}
                          </div>
                          <div
                            className={`${TYPE_SCALE.xs} ${COLOR_SCALE['admin-muted-text']} break-words leading-snug`}
                          >
                            {lang.nativeName}
                          </div>
                        </div>
                      </div>
                      {currentLanguage === lang.code && (
                        <Check
                          className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4} ${COLOR_SCALE['status-success']} ${FLEX_SHRINK['0']} ${MARGIN_LEFT['2']}`}
                        />
                      )}
                    </button>
                  ))}
                </>
              ) : (
                // Currency options with search
                <>
                  {/* Search input */}
                  <div className={`${PADDING_X['3']} ${PADDING_BOTTOM['2']}`}>
                    <div className={POSITION_SCALE.relative}>
                      <Search
                        className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${COLOR_SCALE['icon-muted']} absolute left-3 top-1/2 -translate-y-1/2`}
                      />
                      <Input
                        value={currencySearch}
                        onChange={e => setCurrencySearch(e.target.value)}
                        placeholder={t(
                          'currency',
                          'search_placeholder',
                          'Search currency or country'
                        )}
                        className={`${PADDING_LEFT['8']} ${PADDING_RIGHT['3']} ${TYPE_SCALE.sm} w-full`}
                        size="sm"
                      />
                    </div>
                  </div>

                  {/* Currency list */}
                  {filteredCurrencies.length === 0 ? (
                    <div
                      className={`${PADDING_X['4']} ${PADDING_Y['8']} ${TEXT_ALIGNMENT.center} ${TYPE_SCALE.sm} ${COLOR_SCALE['admin-muted-text']}`}
                    >
                      {t(
                        'currency',
                        'no_results',
                        'No currencies match your search. Try another code or country.'
                      )}
                    </div>
                  ) : (
                    <>
                      {filteredCurrencies.map(currency => {
                        const currencySymbol = getCurrencySymbol(currency);
                        const displayName = displayNames?.of(currency);
                        return (
                          <button
                            key={currency}
                            onClick={() => handleCurrencySelect(currency)}
                            className={`${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${PADDING_X['4']} ${PADDING_Y['3']} hover:${COLOR_SCALE['surface-subtle']} text-sm ${COLOR_SCALE.foreground} hover:${COLOR_SCALE.foreground} transition-colors w-full text-left ${RADIUS_SCALE.md} ${MARGIN_X['1']} focus:outline-none ${INTERACTIVE_STATES.focus.ring} focus-visible:ring-inset focus-visible:ring-gray-400 focus-visible:ring-offset-0`}
                            role="menuitem"
                            aria-current={selectedCurrency === currency ? 'true' : 'false'}
                          >
                            <div
                              className={`${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['3']} ${SIZE_SCALE.minW0} ${FLEX.flex1}`}
                            >
                              {currencySymbol && (
                                <span className={`${TYPE_SCALE.base} font-normal flex-shrink-0`}>
                                  {currencySymbol}
                                </span>
                              )}
                              <div className={`${SIZE_SCALE.minW0} ${FLEX.flex1}`}>
                                <span
                                  className={`${FONT_WEIGHTS.semibold} ${TEXT_TRANSFORM.uppercase}`}
                                >
                                  {currency}
                                </span>
                                {displayName && displayName !== currency && (
                                  <div
                                    className={`${TYPE_SCALE.xs} ${COLOR_SCALE['admin-muted-text']} truncate`}
                                  >
                                    {displayName}
                                  </div>
                                )}
                              </div>
                            </div>
                            {selectedCurrency === currency && (
                              <Check
                                className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} ${COLOR_SCALE['emerald-600']} ${FLEX_SHRINK['0']} ${MARGIN_LEFT['2']}`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

const PreferencesSelectorWithMonitor = withMemoryLeakMonitor(PreferencesSelector, {
  componentName: 'PreferencesSelector',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
});

PreferencesSelector.displayName = 'PreferencesSelector';

export { PreferencesSelectorWithMonitor as PreferencesSelector };
